import { takeLatest, takeEvery, all, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import * as actionTypes from './constants';
import * as actions from './actions';
import {
  selectReseller,
  selectResellers,
} from './selectors';
import { URLToFormData } from '../../utils/helpers';
import ApiService from '../../services/apiService';
import { saveAs } from 'file-saver';
import { toast } from "react-toastify";
import axios from 'axios';
import moment from 'moment';

const scrollToTop = () => {
  document.body.scrollTop = document.documentElement.scrollTop = 0;
}

export function* configRequest() {
  try {
    const config = yield call([ApiService, 'getConfig']);
    yield put(actions.configRequestSuccessAction(config));
  } catch (err) {
    console.log(err);
  } finally {
    yield put(actions.configRequestCompleteAction());
  }
}

export function* updateConfig(action) {
  scrollToTop();
  try {
    const config = yield call([ApiService, 'updateConfig'], action.payload);
    yield put(actions.updateConfigSuccessAction(config));
    toast.success("Saved!");
  } catch (err) {
    toast.error("Error.");
    console.log(err);
  } finally {
    yield put(actions.updateConfigCompleteAction());
  }
}

export function* resellerRequest() {
  try {
    const result = yield call([ApiService, 'getUserItems'], 'reseller');
    yield put(actions.resellerRequestSuccessAction(result));
  } catch (err) {
    throw err;
  } finally {
    yield put(actions.resellerRequestCompleteAction());
  }
}

export function* selectResellerSaga(action) {
  try {
    let reseller;
    if (action.slug === 'add-listing') {
      reseller = {slug: 'add-listing', name: '+ Add Listing', plan: 'free'};
    } else {
      reseller = yield call([ApiService, 'getUserItem'], action.slug, 'reseller');
    }
    yield put(actions.selectResellerSuccess(reseller));
  } catch (error) {
    console.log(error);
  } finally {
    yield put(actions.selectResellerComplete());
  }
}

export function* exportReviews() {
  try {
    const reseller = yield select(selectReseller);
    const reviewsExport = yield call([ApiService, 'reviewsExport'], {reseller: reseller.slug});
    const blob = new Blob([reviewsExport], {type: "text/plain;charset=utf-8"});
    saveAs(blob, "reviews.csv");
    toast.success("Export successful!");
  } catch (err) {
    toast.error("Error.");
    throw err;
  } finally {
    yield put(actions.exportComplete());
  }
}

export function* exportAllReviews() {
  try {
    const reviewsExport = yield call([ApiService, 'allReviewsExport']);
    const blob = new Blob([reviewsExport], {type: "text/plain;charset=utf-8"});
    saveAs(blob, "all-reviews.csv");
    toast.success("Export successful!");
  } catch (err) {
    toast.error("Error.");
    throw err;
  } finally {
    yield put(actions.exportComplete());
  }
}

export function* exportAllResellers() {
  try {
    const reviewsExport = yield call([ApiService, 'allResellersExport']);
    const blob = new Blob([reviewsExport], {type: "text/plain;charset=utf-8"});
    saveAs(blob, "all-resellers.csv");
    toast.success("Export successful!");
  } catch (err) {
    toast.error("Error.");
    throw err;
  } finally {
    yield put(actions.exportComplete());
  }
}

export function* createReseller(action) {
  scrollToTop();
  try {
    const response = yield call([ApiService, 'createItem'], action.reseller, 'reseller');

    if (response.data && response.data.error) {
      toast.error(response.data.error.description || "Error. Not created.");

    } else {
      yield put(actions.createResellerSuccessAction(response));
      yield put(push(`/resellers/${response.slug}/profile`));
      toast.success("Listing created!");
    }

  } catch (err) {
    console.log(err);
    toast.error(err.description || "Error. Not created.");
  } finally {
    yield put(actions.createResellerCompleteAction());
  }
}

export function* uploadEvidence(action) {
  try {
    if (action.payload.length) {
      const resellers = yield select(selectResellers);
      const reseller = yield select(selectReseller);
      const { slug } = reseller;
      const files = action.payload.map(study => study.file.name);
      const signedUrls = yield call([ApiService, 'postEvidence'], {files, slug});
      
      let i = 0;
      for (const study of action.payload) {
        const signedUrl = signedUrls[i];
        const formData = new FormData();
        Object.entries(signedUrl.fields).forEach(([k, v]) => {
          formData.append(k, v);
        });
        formData.append("file", study.file);
        yield call(axios.post, signedUrl.url, formData);
        i++;
      }

      const newCaseStudies = action.payload.map((study, index) => ({
        link: {
          type: 'file',
          location: signedUrls[index].fields.key.split('/')[signedUrls[index].fields.key.split('/').length - 1],
        },
        schoolName: study.title,
        date: moment(study.date, 'YYYY-MM-DD').format('DD/MM/YYYY'),
        location: '',
        type: study.type,
      }));

      const newReseller = {
        ID: reseller.ID,
        TYPE: reseller.TYPE,
        slug: reseller.slug,
        caseStudies: reseller.caseStudies ? [...reseller.caseStudies, ...newCaseStudies] : [...newCaseStudies],
      }

      const result = yield call([ApiService, 'updateReseller'], { reseller: newReseller });

      const updatedResellers = resellers.map(p => {
        if (p.slug === result.slug) return result;
        return p;
      });
      yield put(actions.resellerRequestSuccessAction(updatedResellers));
      yield put(actions.selectResellerSuccess(result));
    }

    toast.success("Saved!");
    
  } catch (err) {
    console.log(err);
    toast.error("Error. Not saved.");
  } finally {
    yield put(actions.completeUpdateReseller());
  }
}

export function* deleteEvidence(action) {
  try {
    const resellers = yield select(selectResellers);
    const { slug } = yield select(selectReseller);
    const result = yield call([ApiService, 'deleteEvidence'], {slug, id: action.payload});
    const updatedResellers = resellers.map(p => {
      if (p.slug === result.slug) return result;
      return p;
    });
    yield put(actions.resellerRequestSuccessAction(updatedResellers));
    yield put(actions.selectResellerSuccess(result));
    toast.success("Deleted!");
    
  } catch (err) {
    console.log(err);
    toast.error("Error. Not deleted.");
  } finally {
    yield put(actions.completeUpdateReseller());
  }
}

export function* updateReseller(action) {
  scrollToTop();
  try {
    const { screenshots, logo } = action;
    const { slug } = yield select(selectReseller);

    const newReseller = {
      ...action.reseller,
      slug,
    };
  
    if (screenshots) {
      const form = new FormData();
      let i = 0;

      for (const image of screenshots) {
        i++;
        const file = yield call(URLToFormData, [image.source]);
        form.append(`file${i}`, file);
      }
      newReseller.images = yield call([ApiService, 'postMedia'], {form, slug, type: 'image', target: 'screenshots'});
    }

    if (logo && logo.source) {
      const form = new FormData();
      const file = yield call(URLToFormData, [logo.source]);
      form.append('file', file);
      newReseller.logo = yield call([ApiService, 'postMedia'], {form, slug, type: 'image', target: 'logo'});
    }
  
    const result = yield call([ApiService, 'updateItem'], newReseller, 'reseller');
    yield put(actions.selectResellerSuccess(result));
    toast.success("Saved!");
    
  } catch (err) {
    toast.error("Error. Not saved.");
    throw err;
  } finally {
    yield put(actions.completeUpdateReseller());
  }
}

export function* reviewsRequest(action) {
  scrollToTop();

  const reseller = action.payload.slug;
  const page = action.payload.page;
  const featured = action.payload.featured;
  const reviewPreference = action.payload.reviewPreference;
  const perPage = action.payload.perPage || 10;

  try {
    const result = yield call([ApiService, 'reviews'], {
      product: reseller,
      page,
      featured,
      reviewPreference,
      perPage,
    });
    yield put(actions.reviewsRequestSuccessAction(result));
  } catch (err) {
    throw err;
  } finally {
    yield put(actions.reviewsRequestCompleteAction());
  }
}

export function* invitesRequest(action) {
  scrollToTop();
  const { page=1, perPage=100, slug } = action.payload;

  try {
    const result = yield call([ApiService, 'getInvites'], {
      slug,
      page,
      perPage,
    });
    yield put(actions.invitesRequestSuccessAction(result));
  } catch (err) {
    throw err;
  } finally {
    yield put(actions.invitesRequestCompleteAction());
  }
}

export function* invitesUpload(action) {
  scrollToTop();
  try {
    const { slug } = yield select(selectReseller);
    const result = yield call([ApiService, 'createInvites'], {
      slug, invites: action.payload.invites, sendDate: action.payload.sendDate,
    });
    yield put(actions.invitesUploadSuccessAction(result));
    toast.success("Success!");
    yield put(push(`/resellers/${slug}/reviews/invitations`));
  } catch (err) {
    toast.error("Error.");
    console.log(err);
  } finally {
    yield put(actions.invitesUploadCompleteAction());
  }
}

export function* reviewReply(action) {
  try {
    const result = yield call([ApiService, 'replyToReview'], action.payload);
    yield put(actions.reviewReplySuccessAction({
      ...action.payload.review,
      reply: action.payload.reply,
    }));
    toast.success("Success!");
  } catch (err) {
    toast.error("Error.");
    console.log(err);
  } finally {
    yield put(actions.reviewReplyCompleteAction());
  }
}

export default function* watch() {
  yield takeLatest(actionTypes.RESELLER_REQUEST, resellerRequest);
  yield takeLatest(actionTypes.CONFIG_REQUEST, configRequest);
  yield takeLatest(actionTypes.CREATE_RESELLER, createReseller);
  yield takeLatest(actionTypes.UPDATE_RESELLER, updateReseller);
  yield takeLatest(actionTypes.SELECT_RESELLER, selectResellerSaga);
  yield takeLatest(actionTypes.EXPORT_REVIEWS, exportReviews);
  yield takeLatest(actionTypes.EXPORT_ALL_REVIEWS, exportAllReviews);
  yield takeLatest(actionTypes.EXPORT_ALL_RESELLERS, exportAllResellers);
  yield takeLatest(actionTypes.REVIEWS_REQUEST, reviewsRequest);
  yield takeLatest(actionTypes.INVITES_REQUEST, invitesRequest);
  yield takeLatest(actionTypes.INVITES_UPLOAD, invitesUpload);
  yield takeLatest(actionTypes.UPLOAD_EVIDENCE, uploadEvidence);
  yield takeLatest(actionTypes.DELETE_EVIDENCE, deleteEvidence);
  yield takeLatest(actionTypes.UPDATE_CONFIG, updateConfig);
  yield takeLatest(actionTypes.REVIEW_REPLY, reviewReply);
}
